import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, Image, Plus, Check, X, LogOut, Camera, CheckSquare, Square, ChevronLeft, ChevronRight, Clock, User, Settings, Shield, Trash2, ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// API helper functions
const api = {
  setToken: (token) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  },
  
  getToken: () => localStorage.getItem('token'),
  
  headers: () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${api.getToken()}`
  }),
  
  // Auth
  login: async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    return res.json();
  },
  
  register: async (name, email, password) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password })
    });
    return res.json();
  },
  
  getCurrentUser: async () => {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  // Events
  getEvents: async () => {
    const res = await fetch(`${API_URL}/api/events`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  createEvent: async (event) => {
    const res = await fetch(`${API_URL}/api/events`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify(event)
    });
    return res.json();
  },
  
  deleteEvent: async (id) => {
    const res = await fetch(`${API_URL}/api/events/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },

  updateEvent: async (id, event) => {
    const res = await fetch(`${API_URL}/api/events/${id}`, {
      method: 'PUT',
      headers: api.headers(),
      body: JSON.stringify(event)
    });
    return res.json();
  },
  
  // Chores
  getChores: async () => {
    const res = await fetch(`${API_URL}/api/chores`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  createChore: async (chore) => {
    const res = await fetch(`${API_URL}/api/chores`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify(chore)
    });
    return res.json();
  },
  
  toggleChore: async (id) => {
    const res = await fetch(`${API_URL}/api/chores/${id}/toggle`, {
      method: 'PATCH',
      headers: api.headers()
    });
    return res.json();
  },
  
  deleteChore: async (id) => {
    const res = await fetch(`${API_URL}/api/chores/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  // Photos
  getPhotos: async () => {
    const res = await fetch(`${API_URL}/api/photos`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  uploadPhoto: async (file, caption) => {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('caption', caption);
    
    const res = await fetch(`${API_URL}/api/photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${api.getToken()}` },
      body: formData
    });
    return res.json();
  },
  
  deletePhoto: async (id) => {
    const res = await fetch(`${API_URL}/api/photos/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  // Family Members
  getFamilyMembers: async () => {
    const res = await fetch(`${API_URL}/api/family-members`, {
      headers: api.headers()
    });
    return res.json();
  },

  // Shopping List
  getShoppingList: async () => {
    const res = await fetch(`${API_URL}/api/shopping`, { headers: api.headers() });
    return res.json();
  },

  addShoppingItem: async (item, quantity) => {
    const res = await fetch(`${API_URL}/api/shopping`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify({ item, quantity })
    });
    return res.json();
  },

  toggleShoppingItem: async (id) => {
    const res = await fetch(`${API_URL}/api/shopping/${id}/toggle`, {
      method: 'PATCH',
      headers: api.headers()
    });
    return res.json();
  },

  deleteShoppingItem: async (id) => {
    const res = await fetch(`${API_URL}/api/shopping/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },

  clearCheckedItems: async () => {
    const res = await fetch(`${API_URL}/api/shopping/clear`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  // Admin
  getUsers: async () => {
    const res = await fetch(`${API_URL}/api/admin/users`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  createUser: async (user) => {
    const res = await fetch(`${API_URL}/api/admin/users`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify(user)
    });
    return res.json();
  },
  
  updateUser: async (id, user) => {
    const res = await fetch(`${API_URL}/api/admin/users/${id}`, {
      method: 'PUT',
      headers: api.headers(),
      body: JSON.stringify(user)
    });
    return res.json();
  },
  
  deleteUser: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/users/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  getStats: async () => {
    const res = await fetch(`${API_URL}/api/admin/stats`, {
      headers: api.headers()
    });
    return res.json();
  }
};

const FamilyCalendar = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuth, setShowAuth] = useState(true);
  const [authMode, setAuthMode] = useState('login');
  const [currentView, setCurrentView] = useState('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Auth form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [authError, setAuthError] = useState('');
  
  // Data state
  const [events, setEvents] = useState([]);
  const [chores, setChores] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  
  // Modal state
  const [showEventModal, setShowEventModal] = useState(false);
  const [showChoreModal, setShowChoreModal] = useState(false);
  const [slideshowMode, setSlideshowMode] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [idleSlideshow, setIdleSlideshow] = useState(false);
  const [slideshowClock, setSlideshowClock] = useState('');

  // Refs for idle timer — keeps callbacks fresh without re-registering listeners
  const idleTimerRef = useRef(null);
  const slideshowModeRef = useRef(false);
  const idleSlideshowRef = useRef(false);
  const photosRef = useRef([]);
  const currentUserRef = useRef(null);
  
  // Form state
  const [eventForm, setEventForm] = useState({ title: '', date: '', time: '', description: '', repeatType: 'none', repeatDays: [], repeatUntil: '', customDates: [] });
  const [customDateInput, setCustomDateInput] = useState('');
  const [choreForm, setChoreForm] = useState({ title: '', assigned_to: '', due_date: '', notes: '' });
  const [shoppingForm, setShoppingForm] = useState({ item: '', quantity: '' });
  
  // Admin state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', is_admin: false });
  const [editingUser, setEditingUser] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Responsive breakpoint listener
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load user data on mount
  useEffect(() => {
    const token = api.getToken();
    if (token) {
      loadCurrentUser();
    }
  }, []);

  // Slideshow timer — advances photo every 5 s
  useEffect(() => {
    if (slideshowMode && photos.length > 0) {
      const timer = setInterval(() => {
        setCurrentPhotoIndex(prev => (prev + 1) % photos.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [slideshowMode, photos.length]);

  // Keep refs in sync so idle callbacks always read current values
  useEffect(() => { slideshowModeRef.current = slideshowMode; }, [slideshowMode]);
  useEffect(() => { idleSlideshowRef.current = idleSlideshow; }, [idleSlideshow]);
  useEffect(() => { photosRef.current = photos; }, [photos]);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);

  // Clock updater — runs only while screensaver is active
  useEffect(() => {
    if (!idleSlideshow) return;
    const fmt = () => new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
    setSlideshowClock(fmt());
    const tick = setInterval(() => setSlideshowClock(fmt()), 1000);
    return () => clearInterval(tick);
  }, [idleSlideshow]);

  // Idle detection — starts screensaver after 2 minutes of no activity
  useEffect(() => {
    const IDLE_MS = 2 * 60 * 1000;

    const startTimer = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (currentUserRef.current && photosRef.current.length > 0 && !slideshowModeRef.current) {
          setCurrentPhotoIndex(0);
          setSlideshowMode(true);
          setIdleSlideshow(true);
        }
      }, IDLE_MS);
    };

    const onActivity = () => {
      // Dismiss screensaver on any interaction
      if (slideshowModeRef.current && idleSlideshowRef.current) {
        setSlideshowMode(false);
        setIdleSlideshow(false);
      }
      startTimer();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    startTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      clearTimeout(idleTimerRef.current);
    };
  }, []); // Runs once on mount; refs keep values fresh

  const loadCurrentUser = async () => {
    try {
      const user = await api.getCurrentUser();
      if (user.error) {
        handleLogout();
        return;
      }
      setCurrentUser(user);
      setShowAuth(false);
      await loadAllData();
    } catch (error) {
      console.error('Load user error:', error);
      handleLogout();
    }
  };

  const loadAllData = async () => {
    try {
      const [eventsData, choresData, photosData, membersData, shoppingData] = await Promise.all([
        api.getEvents(),
        api.getChores(),
        api.getPhotos(),
        api.getFamilyMembers(),
        api.getShoppingList()
      ]);

      console.log('Loaded events:', eventsData);
      console.log('Loaded chores:', choresData);

      setEvents(eventsData);
      setChores(choresData);
      setPhotos(photosData);
      setFamilyMembers(membersData);
      setShoppingList(Array.isArray(shoppingData) ? shoppingData : []);
    } catch (error) {
      console.error('Load data error:', error);
    }
  };

  const loadAdminData = async () => {
    try {
      const [usersData, statsData] = await Promise.all([
        api.getUsers(),
        api.getStats()
      ]);
      
      setUsers(usersData);
      setStats(statsData);
    } catch (error) {
      console.error('Load admin data error:', error);
    }
  };

  useEffect(() => {
    if (currentUser?.is_admin && currentView === 'admin') {
      loadAdminData();
    }
  }, [currentView, currentUser]);

  const handleAuth = async () => {
    try {
      setAuthError('');
      let result;
      
      if (authMode === 'login') {
        result = await api.login(email, password);
      } else {
        result = await api.register(name, email, password);
      }
      
      if (result.error) {
        setAuthError(result.error);
        return;
      }
      
      api.setToken(result.token);
      setCurrentUser(result.user);
      setShowAuth(false);
      await loadAllData();
      
      setEmail('');
      setPassword('');
      setName('');
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError('An error occurred. Please try again.');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    api.setToken(null);
    setShowAuth(true);
    setCurrentView('calendar');
  };

  const addEvent = async () => {
    try {
      const resetForm = { title: '', date: '', time: '', description: '', repeatType: 'none', repeatDays: [], repeatUntil: '', customDates: [] };
      if (editingEvent) {
        const updated = await api.updateEvent(editingEvent.id, eventForm);
        if (!updated.error) {
          setEvents(events.map(e => e.id === editingEvent.id ? updated : e));
        }
      } else {
        // Build the list of dates to create events on
        let datesToCreate = [];
        const pad = n => String(n).padStart(2, '0');

        if (eventForm.repeatType === 'weekly' && eventForm.repeatDays.length > 0 && eventForm.repeatUntil) {
          const end = new Date(eventForm.repeatUntil + 'T00:00:00');
          const cur = new Date(eventForm.date + 'T00:00:00');
          while (cur <= end) {
            if (eventForm.repeatDays.includes(cur.getDay())) {
              datesToCreate.push(`${cur.getFullYear()}-${pad(cur.getMonth()+1)}-${pad(cur.getDate())}`);
            }
            cur.setDate(cur.getDate() + 1);
          }
        } else if (eventForm.repeatType === 'custom' && eventForm.customDates.length > 0) {
          datesToCreate = [...eventForm.customDates];
        } else {
          datesToCreate = [eventForm.date];
        }

        const { repeatType, repeatDays, repeatUntil, customDates, ...baseForm } = eventForm;
        const results = await Promise.all(datesToCreate.map(date => api.createEvent({ ...baseForm, date })));
        const validEvents = results.filter(e => !e.error);
        setEvents(prev => [...prev, ...validEvents]);
      }
      setShowEventModal(false);
      setEventForm(resetForm);
      setEditingEvent(null);
      setCustomDateInput('');
    } catch (error) {
      console.error('Add event error:', error);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await api.deleteEvent(id);
      setEvents(events.filter(e => e.id !== id));
    } catch (error) {
      console.error('Delete event error:', error);
    }
  };

  const addShoppingItem = async () => {
    if (!shoppingForm.item.trim()) return;
    try {
      const newItem = await api.addShoppingItem(shoppingForm.item.trim(), shoppingForm.quantity.trim());
      if (!newItem.error) {
        setShoppingList(prev => [...prev.filter(i => !i.checked), newItem, ...prev.filter(i => i.checked)]);
        setShoppingForm({ item: '', quantity: '' });
      }
    } catch (error) {
      console.error('Add shopping item error:', error);
    }
  };

  const toggleShoppingItem = async (id) => {
    try {
      const updated = await api.toggleShoppingItem(id);
      if (!updated.error) {
        setShoppingList(prev => {
          const updated_list = prev.map(i => i.id === id ? updated : i);
          return [...updated_list.filter(i => !i.checked), ...updated_list.filter(i => i.checked)];
        });
      }
    } catch (error) {
      console.error('Toggle shopping item error:', error);
    }
  };

  const deleteShoppingItem = async (id) => {
    try {
      await api.deleteShoppingItem(id);
      setShoppingList(prev => prev.filter(i => i.id !== id));
    } catch (error) {
      console.error('Delete shopping item error:', error);
    }
  };

  const clearCheckedItems = async () => {
    if (!window.confirm('Remove all checked items from the list?')) return;
    try {
      await api.clearCheckedItems();
      setShoppingList(prev => prev.filter(i => !i.checked));
    } catch (error) {
      console.error('Clear checked items error:', error);
    }
  };

  const addChore = async () => {
    try {
      const newChore = await api.createChore(choreForm);
      if (!newChore.error) {
        setChores([...chores, newChore]);
        setShowChoreModal(false);
        setChoreForm({ title: '', assigned_to: '', due_date: '', notes: '' });
      }
    } catch (error) {
      console.error('Add chore error:', error);
    }
  };

  const toggleChore = async (id) => {
    try {
      const updatedChore = await api.toggleChore(id);
      if (!updatedChore.error) {
        setChores(chores.map(chore => chore.id === id ? updatedChore : chore));
      }
    } catch (error) {
      console.error('Toggle chore error:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      try {
        const newPhoto = await api.uploadPhoto(file, '');
        if (!newPhoto.error) {
          setPhotos([...photos, newPhoto]);
        }
      } catch (error) {
        console.error('Upload photo error:', error);
      }
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    
    try {
      await api.deletePhoto(id);
      setPhotos(photos.filter(p => p.id !== id));
    } catch (error) {
      console.error('Delete photo error:', error);
    }
  };

  const handleUserSubmit = async () => {
    try {
      if (editingUser) {
        const updated = await api.updateUser(editingUser.id, {
          name: userForm.name,
          email: userForm.email,
          is_admin: userForm.is_admin
        });
        if (!updated.error) {
          setUsers(users.map(u => u.id === editingUser.id ? updated : u));
        }
      } else {
        const newUser = await api.createUser(userForm);
        if (!newUser.error) {
          setUsers([...users, newUser]);
        }
      }
      
      setShowUserModal(false);
      setUserForm({ name: '', email: '', password: '', is_admin: false });
      setEditingUser(null);
      await loadAdminData();
    } catch (error) {
      console.error('User submit error:', error);
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user? All their data will be removed.')) return;
    
    try {
      await api.deleteUser(id);
      setUsers(users.filter(u => u.id !== id));
      await loadAdminData();
    } catch (error) {
      console.error('Delete user error:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => {
      // Handle both string dates and Date objects from backend
      const eventDate = typeof event.date === 'string' 
        ? event.date.split('T')[0]  // Extract just the date part if it's an ISO string
        : new Date(event.date).toISOString().split('T')[0];
      return eventDate === dateStr;
    });
  };

  const getChoresForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return chores.filter(chore => {
      // Handle both string dates and Date objects from backend
      const choreDate = typeof chore.due_date === 'string'
        ? chore.due_date.split('T')[0]
        : new Date(chore.due_date).toISOString().split('T')[0];
      return choreDate === dateStr;
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (showAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #FFE5F1 0%, #E5F3FF 50%, #F0E5FF 100%)',
        fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          borderRadius: '32px',
          padding: '48px',
          maxWidth: '440px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.5)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
              borderRadius: '24px',
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(181, 185, 255, 0.3)'
            }}>
              <Calendar size={40} color="#fff" strokeWidth={2.5} />
            </div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #FF9CEE 0%, #B28DFF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              letterSpacing: '-0.02em'
            }}>Family Hub</h1>
            <p style={{ color: '#8B7BA8', fontSize: '15px' }}>
              {authMode === 'login' ? 'Welcome back!' : 'Create your family account'}
            </p>
          </div>

          {authError && (
            <div style={{
              background: '#FFE5E5',
              color: '#D32F2F',
              padding: '12px 16px',
              borderRadius: '12px',
              marginBottom: '16px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {authError}
            </div>
          )}

          {authMode === 'signup' && (
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />
          )}

          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
            style={{
              width: '100%',
              padding: '16px 20px',
              border: '2px solid #F0E5FF',
              borderRadius: '16px',
              fontSize: '15px',
              marginBottom: '16px',
              outline: 'none',
              transition: 'all 0.2s',
              background: '#FAFBFF'
            }}
            onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
            onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
            style={{
              width: '100%',
              padding: '16px 20px',
              border: '2px solid #F0E5FF',
              borderRadius: '16px',
              fontSize: '15px',
              marginBottom: '24px',
              outline: 'none',
              transition: 'all 0.2s',
              background: '#FAFBFF'
            }}
            onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
            onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
          />

          <button
            onClick={handleAuth}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 8px 24px rgba(181, 185, 255, 0.3)',
              letterSpacing: '0.02em'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 32px rgba(181, 185, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 24px rgba(181, 185, 255, 0.3)';
            }}
          >
            {authMode === 'login' ? 'Sign In' : 'Create Account'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button
              onClick={() => {
                setAuthMode(authMode === 'login' ? 'signup' : 'login');
                setAuthError('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#B28DFF',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              {authMode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (slideshowMode) {
    const currentPhoto = photos.length > 0 ? photos[currentPhotoIndex] : null;
    const dateStr = new Date().toLocaleDateString('en-AU', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    return (
      <>
        <style>{`
          @keyframes ssPhotoFade {
            from { opacity: 0; transform: scale(1.04); }
            to   { opacity: 1; transform: scale(1); }
          }
        `}</style>
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: '#0a0a14',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
          onClick={idleSlideshow ? () => { setSlideshowMode(false); setIdleSlideshow(false); } : undefined}
        >
          {currentPhoto ? (
            idleSlideshow ? (
              /* ── SCREENSAVER MODE — full bleed, clock, tap to dismiss ── */
              <>
                <img
                  key={currentPhotoIndex}
                  src={currentPhoto.url}
                  alt="Family photo"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    animation: 'ssPhotoFade 1.2s ease',
                  }}
                />
                {/* Gradient vignette for text readability */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(160deg, rgba(0,0,0,0.6) 0%, transparent 35%, transparent 58%, rgba(0,0,0,0.65) 100%)',
                  pointerEvents: 'none',
                }} />

                {/* Clock — top left */}
                <div style={{
                  position: 'absolute',
                  top: '52px',
                  left: '60px',
                  color: '#fff',
                  textShadow: '0 2px 24px rgba(0,0,0,0.6)',
                  pointerEvents: 'none',
                }}>
                  <div style={{
                    fontSize: '82px',
                    fontWeight: '200',
                    letterSpacing: '-4px',
                    lineHeight: 1,
                    fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
                  }}>
                    {slideshowClock}
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '300',
                    marginTop: '10px',
                    opacity: 0.78,
                    letterSpacing: '0.03em',
                    fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
                  }}>
                    {dateStr}
                  </div>
                </div>

                {/* Photo counter — top right */}
                <div style={{
                  position: 'absolute',
                  top: '52px',
                  right: '48px',
                  background: 'rgba(255,255,255,0.14)',
                  backdropFilter: 'blur(8px)',
                  padding: '6px 16px',
                  borderRadius: '20px',
                  color: 'rgba(255,255,255,0.65)',
                  fontSize: '13px',
                  letterSpacing: '0.05em',
                  fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
                }}>
                  {currentPhotoIndex + 1} / {photos.length}
                </div>

                {/* Caption — bottom centre */}
                {currentPhoto.caption ? (
                  <div style={{
                    position: 'absolute',
                    bottom: '72px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '19px',
                    fontWeight: '300',
                    textShadow: '0 2px 14px rgba(0,0,0,0.7)',
                    textAlign: 'center',
                    maxWidth: '70%',
                    lineHeight: 1.55,
                    pointerEvents: 'none',
                    fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
                  }}>
                    {currentPhoto.caption}
                  </div>
                ) : null}

                {/* Tap to dismiss hint — bottom */}
                <div style={{
                  position: 'absolute',
                  bottom: '28px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'rgba(255,255,255,0.32)',
                  fontSize: '11px',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  pointerEvents: 'none',
                  fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
                }}>
                  Tap anywhere to dismiss
                </div>
              </>
            ) : (
              /* ── MANUAL SLIDESHOW MODE — existing centred look ── */
              <>
                <img
                  key={currentPhotoIndex}
                  src={currentPhoto.url}
                  alt="Family photo"
                  style={{
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                    borderRadius: '24px',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
                    animation: 'ssPhotoFade 0.6s ease',
                  }}
                />
                <button
                  onClick={() => setSlideshowMode(false)}
                  style={{
                    position: 'absolute',
                    top: '32px',
                    right: '32px',
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                >
                  <X size={28} color="#fff" />
                </button>
                <div style={{
                  position: 'absolute',
                  bottom: '32px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  padding: '12px 24px',
                  borderRadius: '24px',
                  color: 'white',
                  fontSize: '14px',
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                }}>
                  {currentPhotoIndex + 1} / {photos.length}
                </div>
              </>
            )
          ) : (
            <div style={{ color: 'white', textAlign: 'center' }}>
              <Camera size={64} color="#666" style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '18px', opacity: 0.6 }}>No photos yet</p>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #FFF5F8 0%, #F0E5FF 50%, #E5F3FF 100%)',
      fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
      padding: isMobile ? '8px' : '20px',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <header style={{
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        borderRadius: isMobile ? '16px' : '24px',
        padding: isMobile ? '10px 14px' : '20px 32px',
        marginBottom: isMobile ? '10px' : '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.5)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)'
          }}>
            <Calendar size={24} color="#fff" strokeWidth={2.5} />
          </div>
          <div>
            <h1 style={{
              fontSize: isMobile ? '18px' : '24px',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #FF9CEE 0%, #B28DFF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: 0,
              letterSpacing: '-0.02em'
            }}>Family Hub</h1>
            {!isMobile && (
              <p style={{ fontSize: '13px', color: '#8B7BA8', margin: 0 }}>
                Welcome, {currentUser.name} {currentUser.is_admin && '👑'}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={handleLogout}
          style={{
            background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
            border: 'none',
            borderRadius: '12px',
            padding: isMobile ? '8px 10px' : '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            color: '#8B7BA8',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >
          <LogOut size={18} />
          {!isMobile && 'Sign Out'}
        </button>
      </header>

      {/* Navigation */}
      <nav style={{
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        borderRadius: isMobile ? '14px' : '20px',
        padding: isMobile ? '6px' : '8px',
        marginBottom: isMobile ? '10px' : '24px',
        display: 'flex',
        gap: isMobile ? '4px' : '8px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.5)'
      }}>
        {[
          { id: 'calendar', icon: Calendar, label: 'Calendar' },
          { id: 'chores', icon: CheckSquare, label: 'Chores' },
          { id: 'photos', icon: Image, label: 'Photos' },
          { id: 'shopping', icon: ShoppingCart, label: 'Shopping' },
          ...(currentUser.is_admin ? [{ id: 'admin', icon: Shield, label: 'Admin' }] : [])
        ].map(item => (
          <button
            key={item.id}
            onClick={() => setCurrentView(item.id)}
            style={{
              flex: 1,
              padding: isMobile ? '10px 0' : '14px 20px',
              border: 'none',
              borderRadius: '12px',
              background: currentView === item.id
                ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)'
                : 'transparent',
              color: currentView === item.id ? 'white' : '#8B7BA8',
              fontWeight: '700',
              fontSize: isMobile ? '11px' : '15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '3px' : '8px',
              transition: 'all 0.3s',
              boxShadow: currentView === item.id ? '0 4px 16px rgba(181, 185, 255, 0.3)' : 'none'
            }}
          >
            <item.icon size={isMobile ? 18 : 20} strokeWidth={2.5} />
            {item.label}
          </button>
        ))}
      </nav>

      {/* Calendar View - keeping existing calendar code */}
      {currentView === 'calendar' && (
        <div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: isMobile ? '16px' : '24px',
            padding: isMobile ? '12px 8px' : '32px',
            marginBottom: isMobile ? '10px' : '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                  style={{
                    background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'translateX(-2px)'}
                  onMouseLeave={(e) => e.target.style.transform = 'translateX(0)'}
                >
                  <ChevronLeft size={20} color="#B28DFF" />
                </button>
                <h2 style={{
                  fontSize: isMobile ? '18px' : '28px',
                  fontWeight: '800',
                  color: '#6B5B95',
                  margin: 0,
                  letterSpacing: '-0.02em'
                }}>
                  {currentDate.toLocaleDateString('en-US', { month: isMobile ? 'short' : 'long', year: 'numeric' })}
                </h2>
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                  style={{
                    background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'translateX(2px)'}
                  onMouseLeave={(e) => e.target.style.transform = 'translateX(0)'}
                >
                  <ChevronRight size={20} color="#B28DFF" />
                </button>
              </div>
              <button
                onClick={() => setShowEventModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                  border: 'none',
                  borderRadius: '14px',
                  padding: isMobile ? '8px 12px' : '12px 24px',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: isMobile ? '13px' : '15px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <Plus size={isMobile ? 16 : 20} strokeWidth={2.5} />
                {isMobile ? 'Add' : 'Add Event'}
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: isMobile ? '3px' : '8px'
            }}>
              {(isMobile
                ? ['S', 'M', 'T', 'W', 'T', 'F', 'S']
                : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
              ).map((day, i) => (
                <div key={i} style={{
                  textAlign: 'center',
                  fontSize: isMobile ? '11px' : '13px',
                  fontWeight: '700',
                  color: '#B28DFF',
                  padding: isMobile ? '6px 0' : '12px 0',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase'
                }}>
                  {day}
                </div>
              ))}
              
              {getDaysInMonth(currentDate).map((date, idx) => {
                const dateEvents = date ? getEventsForDate(date) : [];
                const dateChores = date ? getChoresForDate(date) : [];
                const isToday = date && date.toDateString() === new Date().toDateString();
                const isSelected = date && selectedDate && date.toDateString() === selectedDate.toDateString();
                
                const isDragOver = dragOverDate && date && dragOverDate.toDateString() === date.toDateString();

                return (
                  <div
                    key={idx}
                    onClick={() => date && setSelectedDate(date)}
                    onDragOver={(e) => {
                      if (date) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (!isDragOver) setDragOverDate(date);
                      }
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        setDragOverDate(null);
                      }
                    }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      setDragOverDate(null);
                      if (!date) return;
                      try {
                        const draggedEv = JSON.parse(e.dataTransfer.getData('application/json'));
                        if (!draggedEv) return;
                        // Use local date parts to avoid UTC-offset shifting date by 1 day
                        const pad = n => String(n).padStart(2, '0');
                        const newDateStr = `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}`;
                        const ex = new Date(draggedEv.date);
                        const existingDateStr = `${ex.getFullYear()}-${pad(ex.getMonth()+1)}-${pad(ex.getDate())}`;
                        if (newDateStr === existingDateStr) return;
                        const updated = await api.updateEvent(draggedEv.id, {
                          title: draggedEv.title,
                          date: newDateStr,
                          time: draggedEv.time,
                          description: draggedEv.description || ''
                        });
                        setEvents(prev => prev.map(ev => ev.id === draggedEv.id ? updated : ev));
                        setSelectedDate(date);
                      } catch (err) {
                        console.error('Failed to move event:', err);
                      }
                    }}
                    style={{
                      background: !date ? 'transparent' :
                                  isDragOver ? 'rgba(178, 141, 255, 0.2)' :
                                  isSelected ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)' :
                                  isToday ? 'rgba(181, 185, 255, 0.2)' :
                                  'rgba(255, 255, 255, 0.5)',
                      borderRadius: isMobile ? '8px' : '16px',
                      padding: isMobile ? '4px 3px' : '12px',
                      minHeight: isMobile ? '52px' : '100px',
                      cursor: date ? 'pointer' : 'default',
                      border: isDragOver ? '2px dashed #B28DFF' :
                              isToday ? '2px solid #B5B9FF' : '1px solid rgba(255, 255, 255, 0.3)',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (date && !isSelected) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (date && !isSelected) {
                        e.currentTarget.style.background = isToday ? 'rgba(181, 185, 255, 0.2)' : 'rgba(255, 255, 255, 0.5)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {date && (
                      <>
                        <div style={{
                          fontSize: isMobile ? '11px' : '16px',
                          fontWeight: '700',
                          color: isSelected ? 'white' : '#6B5B95',
                          marginBottom: isMobile ? '2px' : '8px',
                          textAlign: isMobile ? 'center' : 'left'
                        }}>
                          {date.getDate()}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '2px' : '4px' }}>
                          {dateEvents.slice(0, isMobile ? 1 : 2).map(event => (
                            <div
                              key={event.id}
                              draggable={true}
                              onDragStart={(e) => {
                                e.stopPropagation();
                                e.dataTransfer.setData('application/json', JSON.stringify(event));
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onDragEnd={() => setDragOverDate(null)}
                              style={{
                                background: isSelected ? 'rgba(255, 255, 255, 0.3)' : '#FFE5F1',
                                padding: isMobile ? '2px 3px' : '6px 8px',
                                borderRadius: isMobile ? '4px' : '8px',
                                fontSize: isMobile ? '8px' : '11px',
                                fontWeight: '600',
                                color: isSelected ? 'white' : '#B28DFF',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'grab'
                              }}
                            >
                              {event.title}
                            </div>
                          ))}
                          {dateChores.slice(0, 1).map(chore => (
                            <div
                              key={chore.id}
                              style={{
                                background: isSelected ? 'rgba(255, 255, 255, 0.3)' : '#E5F3FF',
                                padding: '6px 8px',
                                borderRadius: '8px',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: isSelected ? 'white' : '#6EB5FF',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              ✓ {chore.title}
                            </div>
                          ))}
                          {(dateEvents.length + dateChores.length > 3) && (
                            <div style={{
                              fontSize: '10px',
                              color: isSelected ? 'rgba(255, 255, 255, 0.7)' : '#B28DFF',
                              fontWeight: '600',
                              textAlign: 'center'
                            }}>
                              +{dateEvents.length + dateChores.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected Date Details */}
          {selectedDate && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '800',
                color: '#6B5B95',
                marginBottom: '24px',
                letterSpacing: '-0.02em'
              }}>
                {formatDate(selectedDate)}
              </h3>
              
              {getEventsForDate(selectedDate).length === 0 && getChoresForDate(selectedDate).length === 0 ? (
                <p style={{ color: '#B28DFF', textAlign: 'center', padding: '40px 0' }}>
                  No events or chores for this day
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {getEventsForDate(selectedDate).map(event => (
                    <div
                      key={event.id}
                      style={{
                        background: 'linear-gradient(135deg, #FFE5F1 0%, #FFF5F8 100%)',
                        padding: '20px',
                        borderRadius: '16px',
                        border: '1px solid rgba(255, 181, 232, 0.3)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <Clock size={16} color="#FFB5E8" />
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#B28DFF' }}>
                            {event.time ? event.time.slice(0, 5) : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => {
                              const dateStr = typeof event.date === 'string'
                                ? event.date.split('T')[0]
                                : new Date(event.date).toISOString().split('T')[0];
                              const timeStr = event.time ? event.time.slice(0, 5) : '';
                              setEditingEvent(event);
                              setEventForm({
                                title: event.title,
                                date: dateStr,
                                time: timeStr,
                                description: event.description || ''
                              });
                              setShowEventModal(true);
                            }}
                            style={{
                              background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                              border: 'none',
                              borderRadius: '10px',
                              padding: '8px 14px',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#B28DFF',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'transform 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => deleteEvent(event.id)}
                            style={{
                              background: 'linear-gradient(135deg, #FFE5E5 0%, #FFD0D0 100%)',
                              border: 'none',
                              borderRadius: '10px',
                              padding: '8px 14px',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: '#D32F2F',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'transform 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      </div>
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#6B5B95',
                        margin: '0 0 8px 0'
                      }}>
                        {event.title}
                      </h4>
                      {event.description && (
                        <p style={{ fontSize: '14px', color: '#8B7BA8', margin: 0 }}>
                          {event.description}
                        </p>
                      )}
                    </div>
                  ))}
                  
                  {getChoresForDate(selectedDate).map(chore => (
                    <div
                      key={chore.id}
                      style={{
                        background: 'linear-gradient(135deg, #E5F3FF 0%, #F0E5FF 100%)',
                        padding: '20px',
                        borderRadius: '16px',
                        border: '1px solid rgba(110, 181, 255, 0.3)',
                        opacity: chore.completed ? 0.6 : 1
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                          onClick={() => toggleChore(chore.id)}
                          style={{
                            background: chore.completed ? 'linear-gradient(135deg, #BFFCC6 0%, #85E3FF 100%)' : 'white',
                            border: chore.completed ? 'none' : '2px solid #B5B9FF',
                            borderRadius: '10px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            flexShrink: 0,
                            transition: 'all 0.2s'
                          }}
                        >
                          {chore.completed && <Check size={20} color="#fff" strokeWidth={3} />}
                        </button>
                        <div style={{ flex: 1 }}>
                          <h4 style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: '#6B5B95',
                            margin: '0 0 4px 0',
                            textDecoration: chore.completed ? 'line-through' : 'none'
                          }}>
                            {chore.title}
                          </h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <User size={14} color="#6EB5FF" />
                            <span style={{ fontSize: '13px', color: '#8B7BA8', fontWeight: '600' }}>
                              {chore.assigned_to}
                            </span>
                          </div>
                        </div>
                      </div>
                      {chore.notes && (
                        <p style={{
                          fontSize: '14px',
                          color: '#8B7BA8',
                          margin: '12px 0 0 44px'
                        }}>
                          {chore.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Chores View - keeping existing chores code */}
      {currentView === 'chores' && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.5)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: '#6B5B95',
              margin: 0,
              letterSpacing: '-0.02em'
            }}>
              Chores
            </h2>
            <button
              onClick={() => setShowChoreModal(true)}
              style={{
                background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                border: 'none',
                borderRadius: '14px',
                padding: '12px 24px',
                color: 'white',
                fontWeight: '700',
                fontSize: '15px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <Plus size={20} strokeWidth={2.5} />
              Add Chore
            </button>
          </div>

          {chores.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#B28DFF' }}>
              <CheckSquare size={48} color="#E0D4F7" style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '16px', fontWeight: '600' }}>No chores yet</p>
              <p style={{ fontSize: '14px', opacity: 0.7 }}>Add your first chore to get started</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {chores.sort((a, b) => new Date(a.due_date) - new Date(b.due_date)).map(chore => (
                <div
                  key={chore.id}
                  style={{
                    background: chore.completed 
                      ? 'linear-gradient(135deg, #BFFCC6 0%, #AFF8DB 100%)'
                      : 'linear-gradient(135deg, #E5F3FF 0%, #F0E5FF 100%)',
                    padding: '24px',
                    borderRadius: '20px',
                    border: `2px solid ${chore.completed ? 'rgba(191, 252, 198, 0.5)' : 'rgba(110, 181, 255, 0.3)'}`,
                    transition: 'all 0.3s',
                    opacity: chore.completed ? 0.7 : 1
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                      onClick={() => toggleChore(chore.id)}
                      style={{
                        background: chore.completed ? 'linear-gradient(135deg, #85E3FF 0%, #6EB5FF 100%)' : 'white',
                        border: chore.completed ? 'none' : '2px solid #B5B9FF',
                        borderRadius: '12px',
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'all 0.2s',
                        boxShadow: chore.completed ? '0 4px 16px rgba(110, 181, 255, 0.3)' : 'none'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    >
                      {chore.completed && <Check size={28} color="#fff" strokeWidth={3} />}
                    </button>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#6B5B95',
                        margin: '0 0 8px 0',
                        textDecoration: chore.completed ? 'line-through' : 'none',
                        letterSpacing: '-0.01em'
                      }}>
                        {chore.title}
                      </h3>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <User size={16} color="#6EB5FF" />
                          <span style={{ fontSize: '14px', color: '#8B7BA8', fontWeight: '600' }}>
                            {chore.assigned_to}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={16} color="#FFB5E8" />
                          <span style={{ fontSize: '14px', color: '#8B7BA8', fontWeight: '600' }}>
                            {new Date(chore.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      </div>
                      {chore.notes && (
                        <p style={{
                          fontSize: '14px',
                          color: '#8B7BA8',
                          margin: '12px 0 0 0',
                          lineHeight: '1.6'
                        }}>
                          {chore.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Photos View - keeping existing photos code */}
      {currentView === 'photos' && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.5)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: '#6B5B95',
              margin: 0,
              letterSpacing: '-0.02em'
            }}>
              Family Photos
            </h2>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label style={{
                background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                border: 'none',
                borderRadius: '14px',
                padding: '12px 24px',
                color: 'white',
                fontWeight: '700',
                fontSize: '15px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <Camera size={20} strokeWidth={2.5} />
                Upload Photos
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
              </label>
              {photos.length > 0 && (
                <button
                  onClick={() => setSlideshowMode(true)}
                  style={{
                    background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                    border: 'none',
                    borderRadius: '14px',
                    padding: '12px 24px',
                    color: '#B28DFF',
                    fontWeight: '700',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  Start Slideshow
                </button>
              )}
            </div>
          </div>

          {photos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#B28DFF' }}>
              <Camera size={48} color="#E0D4F7" style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '16px', fontWeight: '600' }}>No photos yet</p>
              <p style={{ fontSize: '14px', opacity: 0.7 }}>Upload your first family photo</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              {photos.map((photo, idx) => (
                <div
                  key={photo.id}
                  style={{
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                    transition: 'transform 0.2s',
                    cursor: 'pointer',
                    background: 'white',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <img
                    src={photo.url}
                    alt={photo.caption || 'Family photo'}
                    onClick={() => {
                      setCurrentPhotoIndex(idx);
                      setSlideshowMode(true);
                    }}
                    style={{
                      width: '100%',
                      height: '250px',
                      objectFit: 'cover'
                    }}
                  />
                  <button
                    onClick={() => deletePhoto(photo.id)}
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      opacity: 0.8
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                      e.currentTarget.style.background = '#FFE5E5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    <Trash2 size={18} color="#D32F2F" />
                  </button>
                  <div style={{ padding: '16px' }}>
                    <p style={{
                      fontSize: '13px',
                      color: '#8B7BA8',
                      margin: 0,
                      fontWeight: '600'
                    }}>
                      {new Date(photo.created_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: '#B28DFF',
                      margin: '4px 0 0 0'
                    }}>
                      Uploaded by {photo.uploaded_by}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Shopping List View */}
      {currentView === 'shopping' && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.5)'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: '#6B5B95',
                margin: 0,
                letterSpacing: '-0.02em'
              }}>
                Shopping List
              </h2>
              {shoppingList.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{
                    background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '700',
                    padding: '4px 10px',
                    borderRadius: '20px'
                  }}>
                    {shoppingList.filter(i => !i.checked).length} remaining
                  </span>
                  {shoppingList.some(i => i.checked) && (
                    <span style={{
                      background: 'linear-gradient(135deg, #BFFCC6 0%, #AFF8DB 100%)',
                      color: '#2d7a3a',
                      fontSize: '12px',
                      fontWeight: '700',
                      padding: '4px 10px',
                      borderRadius: '20px'
                    }}>
                      {shoppingList.filter(i => i.checked).length} in cart
                    </span>
                  )}
                </div>
              )}
            </div>
            {shoppingList.some(i => i.checked) && (
              <button
                onClick={clearCheckedItems}
                style={{
                  background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '10px 20px',
                  color: '#B28DFF',
                  fontWeight: '700',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <Check size={16} strokeWidth={2.5} />
                Clear Checked
              </button>
            )}
          </div>

          {/* Inline Add Form */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '28px',
            background: 'rgba(255, 255, 255, 0.8)',
            padding: '16px',
            borderRadius: '18px',
            border: '2px solid #F0E5FF',
            alignItems: 'center'
          }}>
            <ShoppingCart size={20} color="#B28DFF" style={{ flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Add an item… (e.g. Milk)"
              value={shoppingForm.item}
              onChange={(e) => setShoppingForm({ ...shoppingForm, item: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && addShoppingItem()}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '15px',
                background: 'transparent',
                color: '#6B5B95',
                fontFamily: 'inherit'
              }}
            />
            <input
              type="text"
              placeholder="Qty (optional)"
              value={shoppingForm.quantity}
              onChange={(e) => setShoppingForm({ ...shoppingForm, quantity: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && addShoppingItem()}
              style={{
                width: '130px',
                border: '2px solid #F0E5FF',
                borderRadius: '10px',
                padding: '8px 12px',
                outline: 'none',
                fontSize: '14px',
                background: '#FAFBFF',
                color: '#6B5B95',
                fontFamily: 'inherit',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />
            <button
              onClick={addShoppingItem}
              disabled={!shoppingForm.item.trim()}
              style={{
                background: shoppingForm.item.trim()
                  ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)'
                  : '#E0D4F7',
                border: 'none',
                borderRadius: '12px',
                padding: '10px 20px',
                color: 'white',
                fontWeight: '700',
                fontSize: '14px',
                cursor: shoppingForm.item.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'transform 0.2s',
                boxShadow: shoppingForm.item.trim() ? '0 4px 14px rgba(181, 185, 255, 0.35)' : 'none',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => { if (shoppingForm.item.trim()) e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Plus size={16} strokeWidth={2.5} />
              Add
            </button>
          </div>

          {/* List */}
          {shoppingList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#B28DFF' }}>
              <ShoppingCart size={48} color="#E0D4F7" style={{ margin: '0 auto 16px', display: 'block' }} />
              <p style={{ fontSize: '16px', fontWeight: '600' }}>Your list is empty</p>
              <p style={{ fontSize: '14px', opacity: 0.7 }}>Add items you need for the weekly shop above</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Unchecked items */}
              {shoppingList.filter(i => !i.checked).map(item => (
                <div
                  key={item.id}
                  style={{
                    background: 'linear-gradient(135deg, #FFF5F8 0%, #F8F0FF 100%)',
                    padding: '16px 20px',
                    borderRadius: '16px',
                    border: '1.5px solid rgba(181, 185, 255, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    transition: 'all 0.2s'
                  }}
                >
                  <button
                    onClick={() => toggleShoppingItem(item.id)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      border: '2px solid #B5B9FF',
                      background: 'white',
                      cursor: 'pointer',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F0E5FF';
                      e.currentTarget.style.borderColor = '#B28DFF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = '#B5B9FF';
                    }}
                    title="Mark as in cart"
                  />
                  <span style={{
                    flex: 1,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6B5B95'
                  }}>
                    {item.item}
                  </span>
                  {item.quantity && (
                    <span style={{
                      background: 'rgba(181, 185, 255, 0.2)',
                      color: '#B28DFF',
                      fontSize: '12px',
                      fontWeight: '700',
                      padding: '3px 10px',
                      borderRadius: '20px',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.quantity}
                    </span>
                  )}
                  <span style={{ fontSize: '12px', color: '#C0B0D8', fontWeight: '500' }}>
                    {item.added_by}
                  </span>
                  <button
                    onClick={() => deleteShoppingItem(item.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#D0C0E0',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#D32F2F'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#D0C0E0'}
                    title="Remove item"
                  >
                    <X size={18} strokeWidth={2.5} />
                  </button>
                </div>
              ))}

              {/* Divider when both checked and unchecked exist */}
              {shoppingList.some(i => !i.checked) && shoppingList.some(i => i.checked) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  margin: '6px 0'
                }}>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(181, 185, 255, 0.2)' }} />
                  <span style={{ fontSize: '12px', color: '#B28DFF', fontWeight: '700', whiteSpace: 'nowrap' }}>
                    ✓ In Cart
                  </span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(181, 185, 255, 0.2)' }} />
                </div>
              )}

              {/* Checked items */}
              {shoppingList.filter(i => i.checked).map(item => (
                <div
                  key={item.id}
                  style={{
                    background: 'linear-gradient(135deg, #EDFFF2 0%, #E8FFF5 100%)',
                    padding: '16px 20px',
                    borderRadius: '16px',
                    border: '1.5px solid rgba(191, 252, 198, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    opacity: 0.75,
                    transition: 'all 0.2s'
                  }}
                >
                  <button
                    onClick={() => toggleShoppingItem(item.id)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      border: 'none',
                      background: 'linear-gradient(135deg, #BFFCC6 0%, #85E3FF 100%)',
                      cursor: 'pointer',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(133, 227, 255, 0.3)'
                    }}
                    title="Unmark"
                  >
                    <Check size={14} color="#fff" strokeWidth={3} />
                  </button>
                  <span style={{
                    flex: 1,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#8B9E8B',
                    textDecoration: 'line-through',
                    textDecorationColor: 'rgba(139, 158, 139, 0.5)'
                  }}>
                    {item.item}
                  </span>
                  {item.quantity && (
                    <span style={{
                      background: 'rgba(191, 252, 198, 0.5)',
                      color: '#5a9e6a',
                      fontSize: '12px',
                      fontWeight: '700',
                      padding: '3px 10px',
                      borderRadius: '20px',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.quantity}
                    </span>
                  )}
                  <button
                    onClick={() => deleteShoppingItem(item.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#C0D4C0',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#D32F2F'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#C0D4C0'}
                    title="Remove item"
                  >
                    <X size={18} strokeWidth={2.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Admin View */}
      {currentView === 'admin' && currentUser.is_admin && (
        <div>
          {/* Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '24px'
          }}>
            {[
              { label: 'Total Users', value: stats.users, color: '#FFB5E8' },
              { label: 'Events', value: stats.events, color: '#B5B9FF' },
              { label: 'Chores', value: stats.chores, color: '#85E3FF' },
              { label: 'Photos', value: stats.photos, color: '#BFFCC6' },
              { label: 'Completed', value: stats.completedChores, color: '#FFC9DE' }
            ].map(stat => (
              <div
                key={stat.label}
                style={{
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '20px',
                  padding: '24px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.5)'
                }}
              >
                <div style={{
                  fontSize: '32px',
                  fontWeight: '800',
                  color: stat.color,
                  marginBottom: '8px'
                }}>
                  {stat.value || 0}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#8B7BA8'
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Users Management */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px'
            }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: '#6B5B95',
                margin: 0,
                letterSpacing: '-0.02em'
              }}>
                User Management
              </h2>
              <button
                onClick={() => {
                  setEditingUser(null);
                  setUserForm({ name: '', email: '', password: '', is_admin: false });
                  setShowUserModal(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '12px 24px',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <Plus size={20} strokeWidth={2.5} />
                Add User
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {users.map(user => (
                <div
                  key={user.id}
                  style={{
                    background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                    padding: '24px',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 181, 232, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#6B5B95',
                        margin: 0
                      }}>
                        {user.name}
                      </h3>
                      {user.is_admin && (
                        <span style={{
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '700'
                        }}>
                          ADMIN
                        </span>
                      )}
                    </div>
                    <p style={{
                      fontSize: '14px',
                      color: '#8B7BA8',
                      margin: '0 0 4px 0'
                    }}>
                      {user.email}
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: '#B28DFF',
                      margin: 0
                    }}>
                      Joined {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setEditingUser(user);
                        setUserForm({
                          name: user.name,
                          email: user.email,
                          password: '',
                          is_admin: user.is_admin
                        });
                        setShowUserModal(true);
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #E5F3FF 0%, #F0E5FF 100%)',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '10px 20px',
                        color: '#6B5B95',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteUser(user.id)}
                      disabled={user.id === currentUser.id}
                      style={{
                        background: user.id === currentUser.id ? '#E0E0E0' : '#FFE5E5',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '10px 20px',
                        color: user.id === currentUser.id ? '#999' : '#D32F2F',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: user.id === currentUser.id ? 'not-allowed' : 'pointer',
                        transition: 'transform 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => {
                        if (user.id !== currentUser.id) {
                          e.target.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showEventModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }} onClick={() => { setShowEventModal(false); setEditingEvent(null); setCustomDateInput(''); setEventForm({ title: '', date: '', time: '', description: '', repeatType: 'none', repeatDays: [], repeatUntil: '', customDates: [] }); }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '32px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}
          >
            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#6B5B95',
              marginBottom: '24px',
              letterSpacing: '-0.02em'
            }}>
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </h3>

            <input
              type="text"
              placeholder="Event title"
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <input
                type="date"
                value={eventForm.date}
                onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                style={{
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
              <input
                type="time"
                value={eventForm.time}
                onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                style={{
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
            </div>

            <textarea
              placeholder="Description (optional)"
              value={eventForm.description}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '20px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                minHeight: '100px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            {/* Repeat Section — only when adding a new event */}
            {!editingEvent && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#9B8EC4', marginBottom: '10px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Repeat
                </div>
                {/* Mode selector */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: eventForm.repeatType !== 'none' ? '14px' : '0' }}>
                  {[['none', 'None'], ['weekly', 'Weekly'], ['custom', 'Custom dates']].map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => {
                        const base = { ...eventForm, repeatType: val, repeatDays: [], repeatUntil: '' };
                        if (val === 'custom') {
                          setEventForm({ ...base, customDates: eventForm.date ? [eventForm.date] : [] });
                        } else {
                          setEventForm({ ...base, customDates: [] });
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 6px',
                        border: eventForm.repeatType === val ? '2px solid #B5B9FF' : '2px solid #F0E5FF',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: eventForm.repeatType === val ? '#6B5B95' : '#B8ADD4',
                        background: eventForm.repeatType === val ? 'linear-gradient(135deg, #F0E5FF 0%, #E5E8FF 100%)' : '#FAFBFF',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Weekly options */}
                {eventForm.repeatType === 'weekly' && (
                  <div>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', justifyContent: 'space-between' }}>
                      {['Su','Mo','Tu','We','Th','Fr','Sa'].map((day, i) => {
                        const sel = eventForm.repeatDays.includes(i);
                        return (
                          <button
                            key={i}
                            onClick={() => {
                              const days = sel
                                ? eventForm.repeatDays.filter(d => d !== i)
                                : [...eventForm.repeatDays, i].sort((a,b) => a-b);
                              setEventForm({ ...eventForm, repeatDays: days });
                            }}
                            style={{
                              width: '38px', height: '38px',
                              borderRadius: '50%',
                              border: sel ? '2px solid #B5B9FF' : '2px solid #F0E5FF',
                              background: sel ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)' : '#FAFBFF',
                              color: sel ? 'white' : '#B8ADD4',
                              fontSize: '12px', fontWeight: '700',
                              cursor: 'pointer', transition: 'all 0.2s', padding: 0
                            }}
                          >{day}</button>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#9B8EC4', whiteSpace: 'nowrap' }}>Until</span>
                      <input
                        type="date"
                        value={eventForm.repeatUntil}
                        min={eventForm.date || undefined}
                        onChange={(e) => setEventForm({ ...eventForm, repeatUntil: e.target.value })}
                        style={{
                          flex: 1, padding: '10px 14px',
                          border: '2px solid #F0E5FF', borderRadius: '12px',
                          fontSize: '14px', outline: 'none',
                          background: '#FAFBFF', fontFamily: 'inherit'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                        onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
                      />
                    </div>
                  </div>
                )}

                {/* Custom dates options */}
                {eventForm.repeatType === 'custom' && (
                  <div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                      <input
                        type="date"
                        value={customDateInput}
                        onChange={(e) => setCustomDateInput(e.target.value)}
                        style={{
                          flex: 1, padding: '10px 14px',
                          border: '2px solid #F0E5FF', borderRadius: '12px',
                          fontSize: '14px', outline: 'none',
                          background: '#FAFBFF', fontFamily: 'inherit'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                        onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
                      />
                      <button
                        onClick={() => {
                          if (customDateInput && !eventForm.customDates.includes(customDateInput)) {
                            setEventForm({ ...eventForm, customDates: [...eventForm.customDates, customDateInput].sort() });
                            setCustomDateInput('');
                          }
                        }}
                        disabled={!customDateInput || eventForm.customDates.includes(customDateInput)}
                        style={{
                          padding: '10px 18px',
                          background: (customDateInput && !eventForm.customDates.includes(customDateInput))
                            ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)' : '#E0D4F7',
                          border: 'none', borderRadius: '12px',
                          fontSize: '14px', fontWeight: '700', color: 'white',
                          cursor: (customDateInput && !eventForm.customDates.includes(customDateInput)) ? 'pointer' : 'not-allowed'
                        }}
                      >Add</button>
                    </div>
                    {eventForm.customDates.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {eventForm.customDates.map(d => (
                          <span key={d} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            padding: '5px 12px',
                            background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                            borderRadius: '20px', fontSize: '13px', fontWeight: '600', color: '#6B5B95'
                          }}>
                            {new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            <span
                              onClick={() => setEventForm({ ...eventForm, customDates: eventForm.customDates.filter(x => x !== d) })}
                              style={{ cursor: 'pointer', color: '#B8ADD4', fontSize: '16px', lineHeight: 1 }}
                            >×</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => { setShowEventModal(false); setEditingEvent(null); setCustomDateInput(''); setEventForm({ title: '', date: '', time: '', description: '', repeatType: 'none', repeatDays: [], repeatUntil: '', customDates: [] }); }}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#B28DFF',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Cancel
              </button>
              {(() => {
                const base = eventForm.title && eventForm.date && eventForm.time;
                const repeatOk = !editingEvent ? (
                  (eventForm.repeatType !== 'weekly' || (eventForm.repeatDays.length > 0 && eventForm.repeatUntil)) &&
                  (eventForm.repeatType !== 'custom' || eventForm.customDates.length > 0)
                ) : true;
                const canSave = base && repeatOk;
                return (
                  <button
                    onClick={addEvent}
                    disabled={!canSave}
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: canSave
                        ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)'
                        : '#E0D4F7',
                      border: 'none',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: '700',
                      color: 'white',
                      cursor: canSave ? 'pointer' : 'not-allowed',
                      transition: 'transform 0.2s',
                      boxShadow: canSave ? '0 8px 24px rgba(181, 185, 255, 0.3)' : 'none'
                    }}
                    onMouseEnter={(e) => { if (canSave) e.target.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    {editingEvent ? 'Save Changes' : (
                      eventForm.repeatType === 'weekly' && eventForm.repeatDays.length > 0 && eventForm.repeatUntil
                        ? 'Add Events'
                        : eventForm.repeatType === 'custom' && eventForm.customDates.length > 0
                          ? `Add ${eventForm.customDates.length} Event${eventForm.customDates.length > 1 ? 's' : ''}`
                          : 'Add Event'
                    )}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Chore Modal */}
      {showChoreModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }} onClick={() => setShowChoreModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '32px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}
          >
            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#6B5B95',
              marginBottom: '24px',
              letterSpacing: '-0.02em'
            }}>
              Add New Chore
            </h3>

            <input
              type="text"
              placeholder="Chore title"
              value={choreForm.title}
              onChange={(e) => setChoreForm({ ...choreForm, title: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Assigned to"
                value={choreForm.assigned_to}
                onChange={(e) => setChoreForm({ ...choreForm, assigned_to: e.target.value })}
                style={{
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
              <input
                type="date"
                value={choreForm.due_date}
                onChange={(e) => setChoreForm({ ...choreForm, due_date: e.target.value })}
                style={{
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
            </div>

            <textarea
              placeholder="Notes (optional)"
              value={choreForm.notes}
              onChange={(e) => setChoreForm({ ...choreForm, notes: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '24px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                minHeight: '100px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowChoreModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#B28DFF',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Cancel
              </button>
              <button
                onClick={addChore}
                disabled={!choreForm.title || !choreForm.assigned_to || !choreForm.due_date}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: choreForm.title && choreForm.assigned_to && choreForm.due_date
                    ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)'
                    : '#E0D4F7',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: 'white',
                  cursor: choreForm.title && choreForm.assigned_to && choreForm.due_date ? 'pointer' : 'not-allowed',
                  transition: 'transform 0.2s',
                  boxShadow: choreForm.title && choreForm.assigned_to && choreForm.due_date 
                    ? '0 8px 24px rgba(181, 185, 255, 0.3)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (choreForm.title && choreForm.assigned_to && choreForm.due_date) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Add Chore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Modal (Admin) */}
      {showUserModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }} onClick={() => setShowUserModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '32px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}
          >
            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#6B5B95',
              marginBottom: '24px',
              letterSpacing: '-0.02em'
            }}>
              {editingUser ? 'Edit User' : 'Add New User'}
            </h3>

            <input
              type="text"
              placeholder="Name"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <input
              type="email"
              placeholder="Email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            {!editingUser && (
              <input
                type="password"
                placeholder="Password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  marginBottom: '16px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
            )}

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px 20px',
              background: '#FAFBFF',
              border: '2px solid #F0E5FF',
              borderRadius: '16px',
              marginBottom: '24px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#B5B9FF'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#F0E5FF'}
            >
              <input
                type="checkbox"
                checked={userForm.is_admin}
                onChange={(e) => setUserForm({ ...userForm, is_admin: e.target.checked })}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#6B5B95' }}>
                Admin privileges
              </span>
            </label>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowUserModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#B28DFF',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Cancel
              </button>
              <button
                onClick={handleUserSubmit}
                disabled={!userForm.name || !userForm.email || (!editingUser && !userForm.password)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: (userForm.name && userForm.email && (editingUser || userForm.password))
                    ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)'
                    : '#E0D4F7',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: 'white',
                  cursor: (userForm.name && userForm.email && (editingUser || userForm.password)) ? 'pointer' : 'not-allowed',
                  transition: 'transform 0.2s',
                  boxShadow: (userForm.name && userForm.email && (editingUser || userForm.password))
                    ? '0 8px 24px rgba(181, 185, 255, 0.3)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (userForm.name && userForm.email && (editingUser || userForm.password)) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                {editingUser ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');
        
        * {
          box-sizing: border-box;
        }
        
        body {
          margin: 0;
          font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="time"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
          filter: opacity(0.6);
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator:hover,
        input[type="time"]::-webkit-calendar-picker-indicator:hover {
          filter: opacity(1);
        }
      `}</style>
    </div>
  );
};

export default FamilyCalendar;